const DATE_FORMAT: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
};

const DATETIME_FORMAT: Intl.DateTimeFormatOptions = {
  ...DATE_FORMAT,
  hour: '2-digit',
  minute: '2-digit',
};

export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-CL', DATE_FORMAT);
}

export function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('es-CL', DATETIME_FORMAT);
}

/**
 * Formats a date-only string ('YYYY-MM-DD') as 'DD-MM-YYYY' WITHOUT going
 * through Date: `new Date('YYYY-MM-DD')` anchors at UTC midnight, which
 * Chile-local renderers display as the previous calendar day.
 */
export function formatDateOnly(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  const [y, m, d] = dateStr.split('-');
  if (!y || !m || !d) return dateStr;
  return `${d}-${m}-${y}`;
}

/**
 * Formats a date-only string ('YYYY-MM-DD') with es-CL styling (month name, etc).
 * Builds the Date from LOCAL y/m/d parts — NEVER `new Date(isoString)`: parsing an
 * ISO instant and rendering it in local time shifts the calendar day for viewers
 * off UTC (the FR-COMUNICACION charla-date bug). Local construct + local format
 * keeps the same day in every timezone.
 */
export function formatDateOnlyEs(
  dateStr: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'long', year: 'numeric' },
): string {
  if (!dateStr) return '-';
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  return new Date(y, m - 1, d).toLocaleDateString('es-CL', opts);
}

/**
 * Converts a date-only string ('YYYY-MM-DD', e.g. from `<input type="date">`)
 * into a NOON-UTC ISO string for sending as fechaProgramada.
 *
 * fechaProgramada is a date-only concept. Sending it as
 * `new Date('YYYY-MM-DD').toISOString()` anchors at UTC midnight, which the
 * execution view and the signed PDF (rendered in America/Santiago, UTC-4/-3)
 * display as the PREVIOUS calendar day. Noon UTC keeps the same calendar day in
 * every renderer (monthly calendar reads UTC date components; execution/PDF read
 * local). Returns undefined for empty input so the field can be omitted.
 */
export function dateOnlyToIso(dateStr: string | null | undefined): string | undefined {
  if (!dateStr) return undefined;
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(Date.UTC(y, m - 1, d, 12, 0, 0)).toISOString();
}

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

export function formatPeriodoLabel(periodo: string): string {
  const parts = periodo.split('-');
  const year = parts[0] ?? '';
  const month = parts[1] ?? '01';
  const idx = parseInt(month, 10) - 1;
  return `${MONTH_NAMES[idx] ?? month} ${year}`;
}

/** Returns the current SIME period as "YYYY-MM" (21st-to-20th boundary). */
export { getCurrentPeriod as getCurrentPeriodo } from './periodo';

/** Relative time label (e.g. "Hace 5 min", "Hace 2h", "Hace 3d"). */
export function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Ahora';
  if (diffMin < 60) return `Hace ${diffMin} min`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `Hace ${diffHr}h`;
  const diffDays = Math.floor(diffHr / 24);
  return `Hace ${diffDays}d`;
}

/**
 * Returns an array of recent periodo strings.
 * @param count - Number of periods to generate.
 * @param ascending - If true, oldest first (useful for charts). Defaults to false (newest first, for dropdowns).
 */
export function getRecentPeriodos(count: number, ascending = false): string[] {
  const periodos: string[] = [];
  const now = new Date();
  let startMonth = now.getMonth(); // 0-indexed
  let startYear = now.getFullYear();
  if (now.getDate() >= 21) {
    startMonth += 1;
    if (startMonth > 11) { startMonth = 0; startYear += 1; }
  }
  if (ascending) {
    for (let i = count - 1; i >= 0; i--) {
      const d = new Date(startYear, startMonth - i, 1);
      periodos.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
  } else {
    for (let i = 0; i < count; i++) {
      const d = new Date(startYear, startMonth - i, 1);
      periodos.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
  }
  return periodos;
}
